import { useState, useEffect } from 'react';

/**
 * A custom React hook that tracks and returns the current width and height of the browser window.
 * It listens for the window resize event and returns the updated dimensions when the window is
 * resized. The update is debounced to prevent unnecessary state updates and re-renders.
 *
 * @param {number} [debounceDelay=300] - The number of milliseconds to delay updating the window
 * size after a resize event. Defaults to 300ms.
 *
 * @returns {Object} The current window size.
 * @returns {number} return.width - The current width of the window in pixels.
 * @returns {number} return.height - The current height of the window in pixels.
 *
 * @example
 * // Basic usage:
 * const { width, height } = useWindowSize();
 * console.log(width, height); // Logs the current width and height of the window
 *
 * @example
 * // With a custom debounce delay:
 * const { width, height } = useWindowSize(500); // 500ms debounce delay
 */
function useWindowSize(debounceDelay = 300) {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    // Check if we are in a client environment before adding the event listener
    if (typeof window === 'undefined') return;

    let timeoutId = null;

    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, debounceDelay);
    }

    window.addEventListener('resize', handleResize);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [debounceDelay]);

  return windowSize;
}

export default useWindowSize;
